/**
 * Serviço de WebSocket para o frontend
 * Gerencia a conexão com o servidor WebSocket para receber notificações em tempo real
 */

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.messageHandlers = [];
    this.reconnectTimer = null;
    this.reconnectInterval = 5000; // 5 segundos
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.urlCandidates = [];
    this.currentUrlIndex = 0;
    this.url = this._getWebSocketUrl();
    this.clientId = null;
    
    // Configurações de heartbeat
    this.pingInterval = null;
    this.pingIntervalMs = 25000; // 25 segundos (menor que o servidor)
    this.pongTimeout = null;
    this.pongTimeoutMs = 10000; // 10 segundos para resposta do servidor
    this.lastPingTime = null;
  }

  /**
   * Função de debug condicional para evitar warnings de eslint
   * @param {string} message - Mensagem a ser logada
   * @param {...any} args - Argumentos adicionais
   * @private
   */
  _debug(message, ...args) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log(message, ...args);
    }
  }

  /**
   * Função de error log condicional
   * @param {string} message - Mensagem de erro
   * @param {...any} args - Argumentos adicionais
   * @private
   */
  _error(message, ...args) {
    // eslint-disable-next-line no-console
    console.error(message, ...args);
  }

  /**
   * Determina a URL do WebSocket com base na URL atual
   * @returns {string} URL do WebSocket
   * @private
   */
  _getWebSocketUrl() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    const port = window.location.port;
    
    // Gerar URLs alternativas para tentar conexão
    this.urlCandidates = [
      `${protocol}//${host}/ws`
    ];
    
    this._debug('WebSocket URLs candidatas:', this.urlCandidates);
    this._debug('Host atual:', host, 'Porta:', port);
    this._debug('Protocolo:', protocol);
    
    // Iniciar com a primeira URL
    return this.urlCandidates[0];
  }

  /**
   * Inicia a conexão com o servidor WebSocket
   */
  connect() {
    // Evitar múltiplas tentativas simultâneas
    if (this.socket || this.isConnecting) {
      this._debug('WebSocket: Conexão já existe ou está sendo tentada');
      return;
    }

    this.isConnecting = true;
    this.reconnectAttempts++;

    try {
      this._debug(`WebSocket: Iniciando conexão (tentativa ${this.reconnectAttempts}) para`, this.url);
      this.socket = new WebSocket(this.url);
      
      // Configurar eventos
      this.socket.onopen = this._onOpen.bind(this);
      this.socket.onmessage = this._onMessage.bind(this);
      this.socket.onclose = this._onClose.bind(this);
      this.socket.onerror = this._onError.bind(this);
      
      this._debug('WebSocket: Conexão iniciada, aguardando abertura...');
    } catch (error) {
      this._error('WebSocket: Erro ao criar conexão:', error.message);
      this.isConnecting = false;
      this._tryNextUrl();
    }
  }

  /**
   * Tenta a próxima URL candidata na lista
   * @private
   */
  _tryNextUrl() {
    // Limpar conexão anterior se existir
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.currentUrlIndex++;
    if (this.currentUrlIndex < this.urlCandidates.length) {
      // Temos mais URLs para tentar
      this.url = this.urlCandidates[this.currentUrlIndex];
      this._debug('WebSocket: Tentando próxima URL:', this.url);
      this.isConnecting = false;
      setTimeout(() => this.connect(), 1000);
    } else {
      // Esgotamos todas as URLs, reiniciar do começo e agendar reconexão
      this._error('WebSocket: Todas as URLs falharam, agendando reconexão...');
      this.currentUrlIndex = 0;
      this.url = this.urlCandidates[0];
      this.isConnecting = false;
      this._scheduleReconnect();
    }
  }

  /**
   * Manipula evento de abertura de conexão
   * @private
   */
  _onOpen() {
    this._debug('Conexão WebSocket estabelecida');
    this.isConnected = true;
    this.isConnecting = false;
    this.reconnectAttempts = 0; // Resetar contador de tentativas
    
    // Limpar timer de reconexão se existir
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Iniciar heartbeat
    this._startHeartbeat();
    
    // Disparar evento de conexão para componentes interessados
    this._notifyHandlers({
      type: 'connection',
      status: 'connected'
    });
  }

  /**
   * Manipula mensagens recebidas
   * @param {MessageEvent} event - Evento de mensagem
   * @private
   */
  _onMessage(event) {
    try {
      const data = JSON.parse(event.data);
      this._debug('WebSocket: Mensagem recebida:', data);
      
      // Tratar mensagens especiais
      if (data.type === 'connection' && data.clientId) {
        this.clientId = data.clientId;
        this._debug('WebSocket: Cliente ID recebido:', this.clientId);
      } else if (data.type === 'pong') {
        this._handlePong();
        return; // Não notificar handlers para pongs
      }
      
      this._notifyHandlers(data);
    } catch (error) {
      this._error('Erro ao processar mensagem WebSocket:', error);
    }
  }

  /**
   * Manipula fechamento de conexão
   * @param {CloseEvent} event - Evento de fechamento
   * @private
   */
  _onClose(event) {
    this._debug(`Conexão WebSocket fechada: código ${event.code}, razão: ${event.reason}`);
    this.isConnected = false;
    this.isConnecting = false;
    this.socket = null;
    this.clientId = null;
    
    // Parar heartbeat
    this._stopHeartbeat();
    
    // Notificar handlers sobre desconexão
    this._notifyHandlers({
      type: 'connection',
      status: 'disconnected',
      code: event.code,
      reason: event.reason
    });
    
    // Agendar reconexão apenas se não foi fechamento intencional e não excedeu o máximo de tentativas
    if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
      this._scheduleReconnect();
    } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this._error(`WebSocket: Máximo de tentativas de reconexão excedido (${this.maxReconnectAttempts})`);
      this._notifyHandlers({
        type: 'connection',
        status: 'failed',
        message: 'Máximo de tentativas de reconexão excedido'
      });
    }
  }

  /**
   * Manipula erros de conexão
   * @param {Event} event - Evento de erro
   * @private
   */
  _onError(event) {
    this._error('WebSocket: Erro na conexão:', event);
    this.isConnecting = false;
    
    // Verificar se a URL pode estar incorreta
    if (this.url.includes('localhost') && window.location.hostname !== 'localhost') {
      this._error('WebSocket: Possível problema na URL - hostname diferente de localhost');
    }
    
    // Verificar se pode ser um problema de CORS ou proxy
    this._debug('WebSocket: Verifique configurações de CORS e proxy no servidor');
    
    // Tentar próxima URL automaticamente
    this._tryNextUrl();
  }

  /**
   * Inicia o mecanismo de heartbeat (ping/pong)
   * @private
   */
  _startHeartbeat() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }

    this.pingInterval = setInterval(() => {
      if (this.isConnected && this.socket) {
        this.lastPingTime = Date.now();
        
        // Enviar ping para o servidor
        try {
          this.socket.send(JSON.stringify({
            type: 'ping',
            timestamp: new Date().toISOString(),
            clientId: this.clientId
          }));
          
          this._debug('WebSocket: Ping enviado');
          
          // Configurar timeout para aguardar pong
          this.pongTimeout = setTimeout(() => {
            this._error('WebSocket: Pong não recebido dentro do timeout, fechando conexão');
            this.socket.close(1006, 'Ping timeout');
          }, this.pongTimeoutMs);
          
        } catch (error) {
          this._error('WebSocket: Erro ao enviar ping:', error);
          this.socket.close(1006, 'Ping error');
        }
      }
    }, this.pingIntervalMs);

    this._debug(`WebSocket: Heartbeat iniciado (${this.pingIntervalMs}ms)`);
  }

  /**
   * Para o mecanismo de heartbeat
   * @private
   */
  _stopHeartbeat() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
    
    this._debug('WebSocket: Heartbeat parado');
  }

  /**
   * Manipula resposta pong do servidor
   * @private
   */
  _handlePong() {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
    
    if (this.lastPingTime) {
      const roundTripTime = Date.now() - this.lastPingTime;
      this._debug(`WebSocket: Pong recebido (RTT: ${roundTripTime}ms)`);
    } else {
      this._debug('WebSocket: Pong recebido');
    }
  }

  /**
   * Agenda uma tentativa de reconexão
   * @private
   */
  _scheduleReconnect() {
    // Evitar múltiplos timers de reconexão
    if (!this.reconnectTimer && !this.isConnecting) {
      // Implementar backoff exponencial limitado
      const backoffDelay = Math.min(this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1), 30000);
      
      this._debug(`Agendando reconexão em ${backoffDelay}ms... (tentativa ${this.reconnectAttempts})`);
      this.reconnectTimer = setTimeout(() => {
        this._debug('Tentando reconectar...');
        this.reconnectTimer = null;
        this.connect();
      }, backoffDelay);
    }
  }

  /**
   * Notifica todos os handlers registrados sobre uma mensagem
   * @param {Object} data - Dados da mensagem
   * @private
   */
  _notifyHandlers(data) {
    this.messageHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        this._error('Erro em handler de mensagem:', error);
      }
    });
  }

  /**
   * Registra um handler para processar mensagens
   * @param {Function} handler - Função que será chamada quando uma mensagem for recebida
   * @returns {Function} Função para remover o handler
   */
  onMessage(handler) {
    if (typeof handler !== 'function') {
      throw new Error('Handler deve ser uma função');
    }
    
    this.messageHandlers.push(handler);
    
    // Retorna função para remover o handler
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index !== -1) {
        this.messageHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Fecha a conexão WebSocket
   */
  disconnect() {
    if (this.socket) {
      this.socket.close(1000, 'Desconexão intencional');
      this.socket = null;
    }
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    this.isConnected = false;
    this.isConnecting = false;
  }
}

// Exporta uma única instância do serviço
const websocketService = new WebSocketService();
export default websocketService;